import React from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

/*
  The one place the dashboard configures toasts.

  Before this, thirteen views each called a bare toast.configure(), so every
  page ran on the library defaults: five seconds for everything, unlimited
  stacking, stock colours. On a money screen a payout rejection vanished
  before it was read and a triple click stacked three copies of the same
  failure beside the warning that preceded it.

  Rules this file encodes:
    - Severity decides how long a toast stays. A success is a glance; an
      error is something the operator acts on, so it stays until dismissed.
    - At most three toasts on screen, newest on top.
    - Every toast is a title plus an optional detail line, in the dashboard
      type scale, so a 160-character server sentence is no longer one line.
    - An error can carry actions (buttons) when the server already knows the
      fix, for example "Use UGX 9,900" on an amount mismatch.
    - Errors do not close on click, so a reference can be selected and copied.
      The close control is always there.

  This module is imported once from index.js so the configuration runs at
  startup; views import { notify } from it and never touch toast directly.
*/

const AUTO_CLOSE_MS = {
  success: 4000,
  info: 5000,
  warning: 8000,
  error: false,
};

if (typeof toast.configure === "function") {
  toast.configure({
    position: "top-right",
    newestOnTop: true,
    limit: 3,
    pauseOnHover: true,
    pauseOnFocusLoss: true,
    draggable: false,
    closeButton: true,
  });
}

function ToastBody({ title, detail, actions, closeToast }) {
  return (
    <div className="lk-toast">
      <div className="lk-toast__title">{title}</div>
      {detail ? <div className="lk-toast__detail">{detail}</div> : null}
      {Array.isArray(actions) && actions.length > 0 ? (
        <div className="lk-toast__actions">
          {actions.map((action) => (
            <button
              key={action.label}
              type="button"
              className={`lk-toast__action${action.primary ? " lk-toast__action--primary" : ""}`}
              onClick={() => {
                action.onClick?.();
                if (action.keepOpen !== true && typeof closeToast === "function") closeToast();
              }}
            >
              {action.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Show a toast of the given severity.
 *
 * @param {"success"|"info"|"warning"|"error"} severity
 * @param {string|React.ReactNode} title  one short line, the thing that happened
 * @param {string|React.ReactNode} [detail]  the reason, figure or next step
 * @param {object} [options]
 * @param {string} [options.id]        toastId: a repeat with the same id updates
 *                                     the toast on screen instead of stacking
 * @param {Array<{label:string,onClick?:Function,primary?:boolean,keepOpen?:boolean}>} [options.actions]
 * @param {number|false} [options.autoClose]  override the severity default
 * @returns the toast id
 */
function show(severity, title, detail, options = {}) {
  const { id, actions, autoClose, ...rest } = options;
  const fn = toast[severity] || toast.info;
  // A bare title stays a string: the CSS renders a string body as the title,
  // and callers (and tests) that look at the message still see plain text.
  const structured = detail != null || (Array.isArray(actions) && actions.length > 0) || typeof title !== "string";
  const content = structured ? <ToastBody title={title} detail={detail} actions={actions} /> : title;
  return fn(content, {
    toastId: id,
    autoClose: autoClose === undefined ? AUTO_CLOSE_MS[severity] : autoClose,
    closeOnClick: severity !== "error",
    ...rest,
  });
}

/** The server's reason for a failed axios call, or the fallback. */
export function reasonFromError(err, fallback = "Something went wrong") {
  return err?.response?.data?.error || err?.response?.data?.message || err?.message || fallback;
}

export const notify = {
  success: (title, detail, options) => show("success", title, detail, options),
  info:    (title, detail, options) => show("info", title, detail, options),
  warn:    (title, detail, options) => show("warning", title, detail, options),
  error:   (title, detail, options) => show("error", title, detail, options),
  /** An error toast built from a failed axios call. */
  fromError: (err, title, options) => show("error", title, reasonFromError(err), options),
  dismiss: (id) => (typeof toast.dismiss === "function" ? toast.dismiss(id) : undefined),
  isActive: (id) => (typeof toast.isActive === "function" ? toast.isActive(id) : false),
};

export default notify;
